import { AnimatePresence, motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { MEADOW_SCENE } from '@/components/blocks/auth/meadow-scene';
import { IconButton, XStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { ConnectStep } from './ConnectStep';
import {
  useFeedOnboardingFlow,
  useFeedOnboardingLayout,
} from './feed-onboarding.hooks';
import { PrepareStep } from './PrepareStep';
import '@/components/blocks/auth/meadow-login.css';

const ProgressDots = ({
  activeIndex,
  count,
}: {
  activeIndex: number;
  count: number;
}) => (
  <XStack className="gap-1.5">
    <span className="sr-only">{`Step ${activeIndex + 1} of ${count}`}</span>
    {Array.from({ length: count }, (_, index) => (
      <motion.span
        key={index}
        aria-hidden
        className={cn(
          'h-1.5 rounded-full transition-colors duration-300',
          index <= activeIndex ? 'bg-accent' : 'bg-border-strongest'
        )}
        initial={false}
        animate={{ width: index === activeIndex ? 22 : 6 }}
        transition={{ duration: 0.3, ease: [0.6, 0.6, 0, 1] }}
      />
    ))}
  </XStack>
);

export const FeedOnboarding = () => {
  const flow = useFeedOnboardingFlow();
  const layout = useFeedOnboardingLayout();
  const isPrepare = flow.step === 'prepare';

  return (
    <div className="meadow-scope meadow-scene relative h-dvh w-full overflow-hidden bg-background text-foreground md:bg-[#1B1C15]">
      <img
        src={MEADOW_SCENE.imageUrl}
        alt=""
        aria-hidden
        className="absolute inset-0 hidden h-full w-full object-cover md:block"
        style={{ objectPosition: MEADOW_SCENE.objectPosition }}
      />
      <div
        className="absolute inset-0 hidden md:block"
        style={{ background: MEADOW_SCENE.wash }}
      />

      <div className="relative z-10 flex h-full w-full items-center justify-center md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={cn(
            'flex h-full w-full flex-col bg-background',
            'px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]',
            'md:max-h-full md:max-w-full md:rounded-2xl md:px-9 md:pb-8 md:pt-5 md:shadow-card',
            layout.isSplit
              ? 'md:h-[38rem] md:w-[68rem]'
              : 'md:h-[52rem] md:w-[42rem]'
          )}
        >
          <XStack className="h-9 shrink-0 items-center justify-between">
            <IconButton
              icon={Icons.chevronLeft}
              aria-label="Back"
              onClick={flow.goBack}
              className={cn(
                'text-foreground-subtle',
                flow.stepIndex === 0 && 'invisible'
              )}
            />
            <ProgressDots activeIndex={flow.stepIndex} count={flow.stepCount} />
            <div className="w-8" />
          </XStack>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={isPrepare ? 'prepare' : 'connect'}
              className="flex min-h-0 flex-1 flex-col"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {isPrepare ? (
                <PrepareStep
                  hasConnectedApps={flow.hasConnectedApps}
                  isFinishing={flow.isFinishing}
                  hasFinishFailed={flow.hasFinishFailed}
                  onFinish={flow.finish}
                />
              ) : (
                <ConnectStep flow={flow} layout={layout} />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};
